import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { translate, translateObject } from '@jsverse/transloco';

import { toCanvas } from 'html-to-image';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';

import { FilterService } from 'app/modules/collection/services/filter.service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { WidgetDefault } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';

import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface WidgetNotesOptions extends WidgetDefault {
  text: string;
  inputMode?: 'html' | 'markdown';
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-notes',
  templateUrl: './widget-notes.component.html',
  styleUrls: ['./widget-notes.component.scss'],
  standalone: false,
})
export class WidgetNotesComponent
  extends AbstractWidgetComponent<WidgetNotesOptions>
  implements OnInit, AfterViewInit
{
  @ViewChild('widgetContainer', { read: ElementRef }) widgetContainer;
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() mode: 'view' | 'edit' = 'view';
  @Input() widgetHeight: number;
  @Output() modeChange = new EventEmitter<'view' | 'edit'>();
  minHeight = 3;
  minWidth = 3;

  constructor(
    private httpClient: HttpClient,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
    this.needDataSource = false;
  }

  ngOnInit() {
    this.dataSourceNotFound = false;
    this.loadingStarted.emit(true);
  }

  ngAfterViewInit() {
    this.loadingFinished.emit(true);
  }

  editNote() {
    this.mode = 'edit';
  }

  onTextChange(value: string) {
    this.widget.payload.text = value;
  }

  onEditFinished() {
    this.mode = 'view';
    this.modeChange.emit(this.mode);
    this.loadingFinished.emit();
  }

  public get text() {
    return this.widget.payload.text;
  }

  get defaultTitle(): string {
    return translate('widgets.note');
  }

  public canShowDataTable() {
    return false;
  }

  public canCreateCollection() {
    return false;
  }

  public canAddToWidgetGroup() {
    return false;
  }

  public canAddToComparison(): boolean {
    return false;
  }

  public isWidgetGroupChangeDisabled(): boolean {
    return true;
  }

  public canChangeAnalyticItem(): boolean {
    return false;
  }

  public canApplyComparison(_comparison?): boolean {
    return false;
  }

  public widgetMenuDisplayOnHover(): boolean {
    return true;
  }

  canHideTitle(): boolean {
    return true;
  }

  get widgetQueryOptions() {
    return null;
  }

  private async captureFullCanvas(): Promise<HTMLCanvasElement> {
    const el: HTMLDivElement = this.widgetContainer.nativeElement;
    const originalOverflow = el.style.overflow;
    const originalHeight = el.style.height;
    const clientHeight = el.clientHeight;
    const scrollHeight = el.scrollHeight;

    el.style.overflow = 'visible';
    el.style.height = scrollHeight > clientHeight ? 'auto' : `${scrollHeight}px`;

    const canvas = await toCanvas(el, { backgroundColor: 'white' });

    el.style.overflow = originalOverflow;
    el.style.height = originalHeight;

    return canvas;
  }

  public async exportCanvases(): Promise<HTMLCanvasElement[]> {
    const el: HTMLDivElement = this.widgetContainer.nativeElement;
    const clientHeight = el.clientHeight;
    const scrollHeight = el.scrollHeight;

    const fullCanvas = await this.captureFullCanvas();
    if (clientHeight === 0 || scrollHeight <= clientHeight) {
      return [fullCanvas];
    }

    const spliceCount = Math.ceil(scrollHeight / clientHeight);
    const sliceHeight = Math.ceil(fullCanvas.height / spliceCount);
    const slices: HTMLCanvasElement[] = [];
    for (let i = 0; i < spliceCount; i++) {
      const srcY = i * sliceHeight;
      const actualHeight = Math.min(sliceHeight, fullCanvas.height - srcY);
      const slice = document.createElement('canvas');
      slice.width = fullCanvas.width;
      slice.height = actualHeight;

      const ctx = slice.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to create 2D canvas context for notes export slice');
      }
      ctx.drawImage(
        fullCanvas,
        0,
        srcY,
        fullCanvas.width,
        actualHeight,
        0,
        0,
        fullCanvas.width,
        actualHeight
      );
      slices.push(slice);
    }

    return slices;
  }

  public async exportAsImage(_format: 'blob' | 'canvas' = 'blob', isPpt?: boolean): Promise<Blob> {
    const currentScrollTop = this.widgetContainer.nativeElement.scrollTop;

    let canvas = await this.captureFullCanvas();
    if (!isPpt) {
      canvas = this.createNewCanvasWithTitle(canvas);
    }

    const contentDataURL = canvas.toDataURL('image/svg');
    const body = {
      filename: this.defaultTitle,
      svg: canvas,
    };

    this.widgetContainer.nativeElement.scroll(0, currentScrollTop);

    return firstValueFrom(
      this.httpClient.post<Blob>(contentDataURL, body, {
        responseType: 'blob' as 'json',
      })
    );
  }

  public addTitleSlideToPptx(pptx) {
    const translations = translateObject('widgets.pptx');
    const rows = [
      [{ text: translations['widget-name'] }, { text: this.widgetTitle }],
      [{ text: translations['dashboard-name'] }, { text: this.dashboard.title }],
      [{ text: translations['generated'] }, { text: moment().format('LLL') }],
    ];

    const dataOptions = {};
    const tableWidth = 12; // in inches
    this.addSlideOptions(dataOptions);

    pptx.addTableSlide(
      rows,
      {
        data: dataOptions,
        autoPageLineWeight: -0.3,
        w: tableWidth,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      },
      true
    );

    return dataOptions;
  }

  public async exportAsPptx(pptx: PptxReport): Promise<void> {
    this.addTitleSlideToPptx(pptx);
    const currentScrollTop = this.widgetContainer.nativeElement.scrollTop;
    const canvases = await this.exportCanvases();
    this.widgetContainer.nativeElement.scroll(0, currentScrollTop);
    for (const canvas of canvases) {
      const contentDataURL = canvas.toDataURL('image/svg');
      const body = {
        filename: this.defaultTitle,
        svg: canvas,
      };

      const blob = await firstValueFrom(
        this.httpClient.post<Blob>(contentDataURL, body, {
          responseType: 'blob' as 'json',
        })
      );
      await this.exportImageToPptx(blob, pptx, this.widgetTitle);
    }
  }
}
